// UpdateRecordConfig.js - Streamlined version with better UX
import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, InputNumber, Tabs, message, Space, Typography, Button } from 'antd';
import JavaScriptExpressionEditor from '../expressionEditor/JavaScriptExpressionEditor';
import AIQuestionsEditor from '../ai/AIQuestionsEditor';
import { SettingOutlined, DownOutlined, RightOutlined } from '@ant-design/icons';
import { useSampleData } from '../expressionEditor/SampleDataContext';
import { useSharedData } from '../../contexts/SharedDataContext';
import { useAuth0 } from '@auth0/auth0-react';
import AIQuestionsTester from '../ai/AIQuestionTester';

const { TabPane } = Tabs;
const { Text } = Typography;
const { TextArea } = Input;

// New component for advanced AI settings
const AdvancedAISettings = ({ form }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="advanced-ai-settings" style={{ marginBottom: 16 }}>
            <div
                onClick={() => setExpanded(!expanded)}
                style={{
                    cursor: 'pointer',
                    color: '#1890ff',
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: expanded ? 16 : 0
                }}
            >
                <SettingOutlined style={{ marginRight: 8 }} />
                <Text strong>Advanced AI Settings</Text>
                {expanded ? <DownOutlined style={{ marginLeft: 8 }} /> : <RightOutlined style={{ marginLeft: 8 }} />}
            </div>

            {expanded && (
                <div style={{
                    padding: '12px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '4px',
                    marginTop: 8
                }}>
                    <Form.Item
                        name="model"
                        label="AI Model"
                        tooltip="Specify the AI model to use"
                    >
                        <Input placeholder="e.g., gpt-4, claude-3-opus" />
                    </Form.Item>

                    <Form.Item
                        name="temperature"
                        label="Temperature"
                        tooltip="Controls randomness (0.0-1.0)"
                    >
                        <InputNumber
                            min={0}
                            max={1}
                            step={0.1}
                            style={{ width: '100px' }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="systemPrompt"
                        label="System Instructions"
                        tooltip="Instructions for the AI model"
                    >
                        <TextArea
                            rows={3}
                            placeholder="You are an assistant analyzing a customer service call. Answer questions based on the call transcript and audio sentiment."
                        />
                    </Form.Item>
                </div>
            )}
        </div>
    );
};

// Enhanced AIQuestionsContainer to better manage spacing and scrolling
const AIQuestionsContainer = () => {
    const { currentSample } = useSampleData();
    const { selectedTenant } = useSharedData();
    const { getAccessTokenSilently } = useAuth0();
    const form = Form.useFormInstance();
    const [testingInProgress, setTestingInProgress] = useState(false);
    const [testResults, setTestResults] = useState({});
    const [testError, setTestError] = useState(null);

    const handleTestAllQuestions = useCallback(async () => {


        // Get all questions from the form, including untouched ones
        const questions = form.getFieldValue('questions') || [];

        console.log("----------- FORM DATA DUMP -----------");
        const allFormValues = form.getFieldsValue();
        console.log("All form values:", allFormValues);

// Get detailed information about questions
        console.log("Questions array:", JSON.stringify(questions, null, 2));

// Inspect each question's allowedValues specifically
        questions.forEach((question, index) => {
            const fieldName = question.fieldName || `Question_${index + 1}`;
            const allowedValues = form.getFieldValue(['questions', index, 'allowedValues']);
            console.log(`Question ${index} (${fieldName}) allowedValues:`, allowedValues);

            // Get raw form data for this question
            const rawQuestionData = form.getFieldValue(['questions', index]);
            console.log(`Question ${index} raw data:`, rawQuestionData);
        });
        console.log("----------- END FORM DATA DUMP -----------");


        // Make sure we have at least one question
        if (questions.length === 0) {
            message.warning('No questions to test');
            return;
        }

        // Ensure we have sample data
        if (!currentSample) {
            message.error('No sample data available for testing');
            return;
        }

        setTestingInProgress(true);
        setTestError(null);

        try {
            // Get auth token from Auth0
            const authToken = await getAccessTokenSilently();

            // Process each question to ensure we have all necessary data
            // This is critical for capturing allowedValues correctly
            const processedQuestions = questions.map((question, index) => {
                // If fieldName is missing, generate one based on index
                if (!question.fieldName) {
                    question.fieldName = `Question_${index + 1}`;
                    // Update the form value to show the new name
                    form.setFieldValue(['questions', index, 'fieldName'], question.fieldName);
                }

                // If question text is missing but we need to include it anyway, provide a placeholder
                if (!question.question) {
                    question.question = 'Enter a question here';
                }

                // Explicitly get allowedValues again to ensure we have the latest data
                const allowedValues = form.getFieldValue(['questions', index, 'allowedValues']) || [];

                // Create a clean question object with all necessary properties
                return {
                    ...question,
                    allowedValues: [...allowedValues] // Ensure we get a fresh copy
                };
            });

            // Log the processed questions for debugging
            console.log('Processed questions for testing:', processedQuestions);

            // Get AI configuration
            const aiConfig = {
                model: form.getFieldValue('model'),
                temperature: form.getFieldValue('temperature'),
                systemPrompt: form.getFieldValue('systemPrompt')
            };

            // Test questions using AI Questions Tester with tenant info and auth token
            const results = await AIQuestionsTester.testQuestions(
                processedQuestions,
                currentSample, // This is our sample call data
                aiConfig,
                selectedTenant, // Pass the tenant from SharedDataContext
                authToken // Pass the Auth0 token
            );
            setTestResults(results);

            message.success('Questions tested successfully');
        } catch (error) {
            console.error('Error testing questions:', error);
            setTestError(error.message || 'An error occurred during testing');
            message.error('Failed to test questions: ' + error.message);
        } finally {
            setTestingInProgress(false);
        }
    }, [form, currentSample, selectedTenant, getAccessTokenSilently]);

    return (
        <div className="ai-questions-container" style={{ marginBottom: 16 }}>
            <Form.Item
                name="answerLocation"
                label="Answer Location"
                initialValue="record.extraData.aiQuestions"
                tooltip="Path where question answers will be stored"
                rules={[{ required: true, message: 'Answer location is required' }]}
                style={{ marginBottom: 16 }}
            >
                <Input placeholder="record.extraData.aiQuestions" />
            </Form.Item>

            <div style={{ marginTop: 8, marginBottom: 16 }}>
                <AIQuestionsEditor
                    nameField="questions"
                    answerLocationField="answerLocation"
                    defaultAnswerLocation="record.extraData.aiQuestions"
                    testQuestion={handleTestAllQuestions}
                    showAnswerLocation={false}
                    testResults={testResults}
                    testingInProgress={testingInProgress}
                    testError={testError}
                />
            </div>

            <AdvancedAISettings form={form} />
        </div>
    );
};

const UpdateRecordConfig = () => {
    const [testingInProgress, setTestingInProgress] = useState(false);
    const form = Form.useFormInstance();
    const { currentSample } = useSampleData();

    // This fixes the Monaco Editor initialization issue when switching tabs
    const [activeTabKey, setActiveTabKey] = useState('javascript');
    const updateType = Form.useWatch('updateType', form) || 'javascript';

    // Set default values based on update type
    useEffect(() => {
        // Update the active tab key to match the form value
        setActiveTabKey(updateType);

        if (updateType === 'javascript' && !form.getFieldValue('script')) {
            form.setFieldValue('script', `// Return an object with fields to update
return {
  extraData: {
    processed: true,
    processedDate: new Date().toISOString()
  }
};`);
        }

        if (updateType === 'ai') {
            if (!form.getFieldValue('model')) {
                form.setFieldValue('model', 'gpt-4o');
            }
            if (!form.getFieldValue('temperature') && form.getFieldValue('temperature') !== 0) {
                form.setFieldValue('temperature', 0.7);
            }
            if (!form.getFieldValue('systemPrompt')) {
                form.setFieldValue('systemPrompt', 'You are an assistant analyzing a customer service call. Answer questions based on the call transcript and audio sentiment.');
            }
        }
    }, [updateType, form]);

    // Handle tab change with delay to allow proper cleanup
    const handleTabChange = (key) => {
        setActiveTabKey(key);
        // Set the form value after a slight delay to ensure Monaco editor cleanup
        setTimeout(() => {
            form.setFieldValue('updateType', key);
        }, 10);
    };

    return (
        <div className="update-record-config">
            {/* Hidden field to store the current updateType */}
            <Form.Item name="updateType" hidden={true}>
                <input />
            </Form.Item>

            <Tabs activeKey={activeTabKey} onChange={handleTabChange} destroyInactiveTabPane={true}>
                <TabPane tab="JavaScript" key="javascript">
                    <Space direction="vertical" style={{ width: '100%', paddingTop: 8 }}>
                        <Text>Write JavaScript to update record fields</Text>
                        <Form.Item
                            name="script"
                            tooltip="Return an object with fields to update"
                            rules={[{ required: true, message: 'Please enter a script' }]}
                        >
                            <JavaScriptExpressionEditor
                                multiline={true}
                                height={200}
                                evaluateOnChange={true}
                            />
                        </Form.Item>
                    </Space>
                </TabPane>

                <TabPane tab="AI" key="ai">
                    <AIQuestionsContainer />
                </TabPane>
            </Tabs>
        </div>
    );
};

export default UpdateRecordConfig;
